import type {
  DocumentNode,
} from '@apollo/client';
import type {
  useQuery,
  useMutation,
  useLazyQuery
} from '@apollo/client/react'
import isNil from 'lodash/isNil';
import type { UnionToIntersection } from 'type-fest';

import type {
  useAdminProjectsColorsProviderAllProjectsQuery,
  useAdminTimeReportBlockedMessageSpaceOwnerQuery,
  useAdminTimeReportOnCallDetailsModalQuery,
  useAdminBenefitSectionsAssignedBenefitVariantsQuery,
  useAdminTimeReportEntryModalLimitsQuery,
  useAdminInvoicesDrawerCreateInvoiceMutation,
  useAdminInvoicesDrawerRemoveInvoiceMutation,
  useAdminInvoicesDrawerUpdateInvoiceMutation,
  useInvoicesDrawerInvoicesLazyQuery as useAdminInvoicesDrawerInvoicesLazyQuery,
  useAdminTimeReportFinancesLazyQuery,
  useAdminTimeReportLeaveAndTrainingQuery,
  useAdminDirectReportingStructureQuery,
  useAdminTimeReportStatsLazyQuery,
  useTimeReportWorkTimeInsightsLazyQuery as useAdminTimeReportWorkTimeInsightsLazyQuery,
  useAdminPreviousMonthSalaryStateQuery,
  useAdminCopyrightsDrawerCreateWorkTransferMutation,
  useAdminCopyrightsDrawerUpdateWorkTransferMutation,
  useAdminCopyrightsDrawerRemoveWorkTransferMutation,
  useCopyrightsDrawerCopyrightsLazyQuery as useAdminCopyrightsDrawerCopyrightsLazyQuery,
  useAdminCopyrightsDrawerMarkNoWorkMutation,
  useCopyrightsTrButtonQuery as useAdminCopyrightsTrButtonQuery,
  useAdminAdditionalCostsDrawerLazyQuery,
  useAdminTimeReportEntryModalUpdateAbsenceRecordMutation,
  useAdminTimeReportEntryModalCreateSingularAbsenceRecordMutation,
  useAdminTimeReportEntryModalCreateAbsenceRecordMutation
} from '../schema/admin';
import type {
  Exact,
  useUserProjectsColorsProviderAllProjectsQuery,
  useUserTimeReportBlockedMessageSpaceOwnerQuery,
  useUserTimeReportOnCallDetailsModalQuery,
  useUserBenefitSectionsAssignedBenefitVariantsQuery,
  useUserTimeReportEntryModalLimitsQuery,
  useUserInvoicesDrawerCreateInvoiceMutation,
  useUserInvoicesDrawerRemoveInvoiceMutation,
  useUserInvoicesDrawerUpdateInvoiceMutation,
  useInvoicesDrawerInvoicesLazyQuery as useUserInvoicesDrawerInvoicesLazyQuery,
  useUserTimeReportFinancesLazyQuery,
  useUserTimeReportLeaveAndTrainingQuery,
  useUserDirectReportingStructureQuery,
  useUserTimeReportStatsLazyQuery,
  useTimeReportWorkTimeInsightsLazyQuery as useUserTimeReportWorkTimeInsightsLazyQuery,
  useUserPreviousMonthSalaryStateQuery,
  useUserCopyrightsDrawerCreateWorkTransferMutation,
  useUserCopyrightsDrawerUpdateWorkTransferMutation,
  useUserCopyrightsDrawerRemoveWorkTransferMutation,
  useCopyrightsDrawerCopyrightsLazyQuery as useUserCopyrightsDrawerCopyrightsLazyQuery,
  useUserCopyrightsDrawerMarkNoWorkMutation,
  useCopyrightsTrButtonQuery as useUserCopyrightsTrButtonQuery,
  useUserAdditionalCostsDrawerLazyQuery,
  useUserTimeReportEntryModalUpdateAbsenceRecordMutation,
  useUserTimeReportEntryModalCreateSingularAbsenceRecordMutation,
  useUserTimeReportEntryModalCreateAbsenceRecordMutation,
} from '../schema/user';
import { AccessModes, parseAccessRights, type ParseAccessRights } from '../services/authorization';
import type { ErrorTuple } from '../services/errors';
import { splitError } from '../services/errors';
import type { Literal } from '../services/guards';
import type { ExtractErrorTuple } from '../services/hooks/useToasts';
import type {
  GetPathValue, MergeDeepObject, ObjectValues, ToPathObject, Nullable,
} from '../services/object';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ApolloOptsType = any;
type EmptyVariablesType = Readonly<{}>;

type AnyHookShape = (baseOptions: ApolloOptsType) => unknown;

type ReplaceEmptyVariables<Variables> = string extends keyof Variables ? EmptyVariablesType : Variables;

type InferVariables<Hook extends AnyHookShape> =
  Parameters<Hook>[0] extends useQuery.Options<ApolloOptsType, infer Variables>
    ? ReplaceEmptyVariables<Variables>
    : NonNullable<Parameters<Hook>[0]> extends useMutation.Options<ApolloOptsType, infer Variables>
      ? ReplaceEmptyVariables<Variables>
      : NonNullable<Parameters<Hook>[0]> extends useLazyQuery.Options<ApolloOptsType, infer Variables>
        ? ReplaceEmptyVariables<Variables>
        : EmptyVariablesType;

type InterchangeableHooks<HookA extends AnyHookShape, HookB extends AnyHookShape> = Readonly<{
  hookA: HookA; hookB: HookB;
}>;

type QueryCreatorConfig = Readonly<{
  withParsedAccessRights: boolean;
  withErrorSplit: boolean;
}>;

type InterchangeableQueriesFromQueryTypes<
  HookA extends AnyHookShape,
  QueryA,
  HookB extends AnyHookShape,
  QueryB,
  Config extends QueryCreatorConfig,
> = Readonly<{
  hook: (...args: ApolloOptsType[]) => ReturnType<HookA> | ReturnType<HookB>;
  splitError: false extends Config['withErrorSplit'] ? null
    : false extends Config['withParsedAccessRights']
      ? (response?: Nullable<QueryA | QueryB>) => ErrorTuple<ExtractErrorTuple<QueryA>>
      | ErrorTuple<ExtractErrorTuple<QueryB>>
      : (response?: Nullable<QueryA | QueryB>) => ErrorTuple<
        ParseAccessRights<ExtractErrorTuple<QueryA>, AccessModes.assumeHandled>>
      | ErrorTuple<ParseAccessRights<ExtractErrorTuple<QueryB>, AccessModes.assumeHandled>>;
}>;

type InterchangeableQueries<HookA extends AnyHookShape, HookB extends AnyHookShape,
  Config extends QueryCreatorConfig> =
  ReturnType<HookA> extends useQuery.Result<infer QueryA, ApolloOptsType>
    ? ReturnType<HookB> extends useQuery.Result<infer QueryB, ApolloOptsType>
      ? InterchangeableQueriesFromQueryTypes<HookA, QueryA, HookB, QueryB, Config>
      : never
    : ReturnType<HookA> extends useLazyQuery.ResultTuple<infer QueryA, ApolloOptsType>
      ? ReturnType<HookB> extends useLazyQuery.ResultTuple<infer QueryB, ApolloOptsType>
        ? InterchangeableQueriesFromQueryTypes<HookA, QueryA, HookB, QueryB, Config>
        : never
      : ReturnType<HookA> extends useMutation.ResultTuple<infer MutationA, ApolloOptsType>
        ? ReturnType<HookB> extends useMutation.ResultTuple<infer MutationB, ApolloOptsType>
          ? InterchangeableQueriesFromQueryTypes<HookA, MutationA, HookB, MutationB, Config>
          : never
        : never;

type MergeVariables<VariablesA, VariablesB> = VariablesA extends Exact<infer VarA>
  ? VariablesB extends Exact<infer VarB>
    ? Partial<MergeDeepObject<VarA, VarB>>
    : Partial<VarA>
  : VariablesB extends Exact<infer VarB>
    ? Partial<VarB>
    : never;

type Hooks = keyof RequiredHooksConfig['gql']['mutations'] | keyof RequiredHooksConfig['gql']['queries'];
type EmptyQueryCreatorConfig = {
  withErrorSplit: false;
  withParsedAccessRights: false;
};

type ConfigsShape = Readonly<{
  [key in Hooks]: QueryCreatorConfig
}>;

type DeriveRequiredHooks<T, Configs extends ConfigsShape, Config extends QueryCreatorConfig> =
  T extends InterchangeableHooks<infer HookA, infer HookB>
    ? InterchangeableQueries<HookA, HookB, Config>
    : T extends Literal ? {
      [key in keyof T]: key extends keyof ConfigsShape
        ? DeriveRequiredHooks<T[key], Configs, Configs[key]>
        : DeriveRequiredHooks<T[key], Configs, EmptyQueryCreatorConfig>
    } : T;

type DeriveAllPossibleVariables<T extends Literal> = UnionToIntersection<ObjectValues<{
  [key in keyof ToPathObject<T, 3>]: GetPathValue<T, key> extends InterchangeableHooks<infer HookA, infer HookB>
    ? MergeVariables<InferVariables<HookA>, InferVariables<HookB>> : never;
}>>;

// type NonSplittableErrors<Config, HooksToOmit> = Config extends InterchangeableQueries<infer HookA, infer HookB>
//   ? InterchangeableQueries<HookA, HookB>
//   : Config extends Literal ? {
//     [key in keyof Config]: key extends HooksToOmit
//       ? Omit<NonSplittableErrors<Config[key], HooksToOmit>, 'splitError'>
//       : NonSplittableErrors<Config[key], HooksToOmit>;
//   } : Config;

const makeQueryConfig = <T extends ConfigsShape>(arg: T) => arg;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- referenced via typeof in RequiredHooks
const queryConfig = makeQueryConfig({
  useBenefitSectionsAssignedBenefitVariantsQuery: { withErrorSplit: false, withParsedAccessRights: false },
  useTimeReportEntryModalLimitsQuery: { withErrorSplit: false, withParsedAccessRights: false },
  useTimeReportFinances: { withErrorSplit: false, withParsedAccessRights: false },
  useTimeReportLeaveAndTrainingQuery: { withErrorSplit: false, withParsedAccessRights: false },
  useDirectReportingStructureQuery: { withErrorSplit: true, withParsedAccessRights: true },
  useInvoicesDrawerInvoicesLazyQuery: { withErrorSplit: false, withParsedAccessRights: false },
  useInvoicesDrawerCreateInvoiceMutation: { withErrorSplit: true, withParsedAccessRights: false },
  useInvoicesDrawerRemoveInvoiceMutation: { withErrorSplit: true, withParsedAccessRights: false },
  useInvoicesDrawerUpdateInvoiceMutation: { withErrorSplit: true, withParsedAccessRights: false },
  useOnCallMultipliers: { withErrorSplit: true, withParsedAccessRights: false },
  useProjectColors: { withErrorSplit: true, withParsedAccessRights: false },
  useUserSpaceOwner: { withErrorSplit: true, withParsedAccessRights: false },
  useTimeReportStats: { withErrorSplit: false, withParsedAccessRights: true },
  useTimeReportWorkTimeInsightsLazyQuery: { withErrorSplit: true, withParsedAccessRights: true },
  usePreviousMonthSalaryStateQuery: { withErrorSplit: false, withParsedAccessRights: true },
  useCopyrightsDrawerCreateWorkTransferMutation: { withErrorSplit: true, withParsedAccessRights: false },
  useCopyrightsDrawerRemoveWorkTransferMutation: { withErrorSplit: true, withParsedAccessRights: false },
  useCopyrightsDrawerUpdateWorkTransferMutation: { withErrorSplit: true, withParsedAccessRights: false },
  useCopyrightsDrawerMarkNoTransferMutation: { withErrorSplit: true, withParsedAccessRights: false },
  useCopyrightsDrawerCopyrightsLazyQuery: { withErrorSplit: false, withParsedAccessRights: false },
  useCopyrightsTrButtonQuery: { withErrorSplit: false, withParsedAccessRights: false },
  useAdditionalCostsLazyQuery: { withErrorSplit: true, withParsedAccessRights: true },
  useTimeReportEntryModalUpdateAbsenceRecordMutation: { withErrorSplit: true, withParsedAccessRights: true },
  useTimeReportEntryModalCreateSingularAbsenceRecordMutation: { withErrorSplit: true, withParsedAccessRights: true },
  useTimeReportEntryModalCreateAbsenceRecordMutation: { withErrorSplit: true, withParsedAccessRights: true }
});

type RequiredHooksConfig = Readonly<{
  gql: {
    queries: {
      useProjectColors: InterchangeableHooks<
        typeof useAdminProjectsColorsProviderAllProjectsQuery,
        typeof useUserProjectsColorsProviderAllProjectsQuery
      >;
      useUserSpaceOwner: InterchangeableHooks<
        typeof useAdminTimeReportBlockedMessageSpaceOwnerQuery,
        typeof useUserTimeReportBlockedMessageSpaceOwnerQuery
      >;
      useOnCallMultipliers: InterchangeableHooks<
        typeof useAdminTimeReportOnCallDetailsModalQuery,
        typeof useUserTimeReportOnCallDetailsModalQuery
      >;
      useTimeReportFinances: InterchangeableHooks<
        typeof useAdminTimeReportFinancesLazyQuery,
        typeof useUserTimeReportFinancesLazyQuery
      >;
      useBenefitSectionsAssignedBenefitVariantsQuery: InterchangeableHooks<
        typeof useAdminBenefitSectionsAssignedBenefitVariantsQuery,
        typeof useUserBenefitSectionsAssignedBenefitVariantsQuery
      >;
      useTimeReportEntryModalLimitsQuery: InterchangeableHooks<
        typeof useAdminTimeReportEntryModalLimitsQuery,
        typeof useUserTimeReportEntryModalLimitsQuery
      >;
      useInvoicesDrawerInvoicesLazyQuery: InterchangeableHooks<
        typeof useAdminInvoicesDrawerInvoicesLazyQuery,
        typeof useUserInvoicesDrawerInvoicesLazyQuery
      >;
      useCopyrightsDrawerCopyrightsLazyQuery: InterchangeableHooks<
        typeof useAdminCopyrightsDrawerCopyrightsLazyQuery,
        typeof useUserCopyrightsDrawerCopyrightsLazyQuery
      >;
      useTimeReportLeaveAndTrainingQuery: InterchangeableHooks<
        typeof useAdminTimeReportLeaveAndTrainingQuery,
        typeof useUserTimeReportLeaveAndTrainingQuery
      >;
      useDirectReportingStructureQuery: InterchangeableHooks<
        typeof useAdminDirectReportingStructureQuery,
        typeof useUserDirectReportingStructureQuery
      >;
      useTimeReportStats: InterchangeableHooks<
        typeof useAdminTimeReportStatsLazyQuery,
        typeof useUserTimeReportStatsLazyQuery
      >;
      useTimeReportWorkTimeInsightsLazyQuery: InterchangeableHooks<
        typeof useAdminTimeReportWorkTimeInsightsLazyQuery,
        typeof useUserTimeReportWorkTimeInsightsLazyQuery
      >;
      usePreviousMonthSalaryStateQuery: InterchangeableHooks<
        typeof useAdminPreviousMonthSalaryStateQuery,
        typeof useUserPreviousMonthSalaryStateQuery
      >;
      useCopyrightsTrButtonQuery: InterchangeableHooks<
        typeof useUserCopyrightsTrButtonQuery,
        typeof useAdminCopyrightsTrButtonQuery
      >;
      useAdditionalCostsLazyQuery: InterchangeableHooks<
        typeof useUserAdditionalCostsDrawerLazyQuery,
        typeof useAdminAdditionalCostsDrawerLazyQuery
      >
    };
    mutations: {
      useInvoicesDrawerCreateInvoiceMutation: InterchangeableHooks<
        typeof useAdminInvoicesDrawerCreateInvoiceMutation,
        typeof useUserInvoicesDrawerCreateInvoiceMutation
      >;
      useInvoicesDrawerRemoveInvoiceMutation: InterchangeableHooks<
        typeof useAdminInvoicesDrawerRemoveInvoiceMutation,
        typeof useUserInvoicesDrawerRemoveInvoiceMutation
      >;
      useInvoicesDrawerUpdateInvoiceMutation: InterchangeableHooks<
        typeof useAdminInvoicesDrawerUpdateInvoiceMutation,
        typeof useUserInvoicesDrawerUpdateInvoiceMutation
      >;
      useCopyrightsDrawerCreateWorkTransferMutation: InterchangeableHooks<
        typeof useAdminCopyrightsDrawerCreateWorkTransferMutation,
        typeof useUserCopyrightsDrawerCreateWorkTransferMutation
      >;
      useCopyrightsDrawerUpdateWorkTransferMutation: InterchangeableHooks<
        typeof useAdminCopyrightsDrawerUpdateWorkTransferMutation,
        typeof useUserCopyrightsDrawerUpdateWorkTransferMutation
      >;
      useCopyrightsDrawerRemoveWorkTransferMutation: InterchangeableHooks<
        typeof useAdminCopyrightsDrawerRemoveWorkTransferMutation,
        typeof useUserCopyrightsDrawerRemoveWorkTransferMutation
      >;
      useCopyrightsDrawerMarkNoTransferMutation: InterchangeableHooks<
        typeof useAdminCopyrightsDrawerMarkNoWorkMutation,
        typeof useUserCopyrightsDrawerMarkNoWorkMutation
      >;
      useTimeReportEntryModalUpdateAbsenceRecordMutation: InterchangeableHooks<
        typeof useAdminTimeReportEntryModalUpdateAbsenceRecordMutation,
        typeof useUserTimeReportEntryModalUpdateAbsenceRecordMutation
      >
      useTimeReportEntryModalCreateSingularAbsenceRecordMutation: InterchangeableHooks<
        typeof useUserTimeReportEntryModalCreateSingularAbsenceRecordMutation,
        typeof useAdminTimeReportEntryModalCreateSingularAbsenceRecordMutation
      >
      useTimeReportEntryModalCreateAbsenceRecordMutation: InterchangeableHooks<
        typeof useAdminTimeReportEntryModalCreateAbsenceRecordMutation,
        typeof useUserTimeReportEntryModalCreateAbsenceRecordMutation
      >
    };
  };
}>;

export type RequiredDocuments = Readonly<{
  userProjectsIds: DocumentNode;
  timeReportEntryModalLimits: DocumentNode;
  assignedBenefits: DocumentNode;
}>;

export type RequiredHooks = DeriveRequiredHooks<RequiredHooksConfig, typeof queryConfig, EmptyQueryCreatorConfig>;
export type CommonVariables = DeriveAllPossibleVariables<RequiredHooksConfig['gql']>;

export type MakeHooksConfigArg = Readonly<{
  [key in keyof RequiredHooks['gql']['mutations']]: RequiredHooks['gql']['mutations'][key]['hook'];
}> & Readonly<{
  [key in keyof RequiredHooks['gql']['queries']]: RequiredHooks['gql']['queries'][key]['hook']
}>;

export const makeHooksConfig = ({
  useProjectColors,
  useUserSpaceOwner,
  useOnCallMultipliers,
  useTimeReportFinances,
  useBenefitSectionsAssignedBenefitVariantsQuery,
  useTimeReportEntryModalLimitsQuery,
  useInvoicesDrawerCreateInvoiceMutation,
  useInvoicesDrawerRemoveInvoiceMutation,
  useInvoicesDrawerUpdateInvoiceMutation,
  useInvoicesDrawerInvoicesLazyQuery,
  useTimeReportLeaveAndTrainingQuery,
  useDirectReportingStructureQuery,
  useTimeReportStats,
  useTimeReportWorkTimeInsightsLazyQuery,
  usePreviousMonthSalaryStateQuery,
  useCopyrightsDrawerCreateWorkTransferMutation,
  useCopyrightsDrawerRemoveWorkTransferMutation,
  useCopyrightsDrawerUpdateWorkTransferMutation,
  useCopyrightsDrawerCopyrightsLazyQuery,
  useCopyrightsDrawerMarkNoTransferMutation,
  useCopyrightsTrButtonQuery,
  useAdditionalCostsLazyQuery,
  useTimeReportEntryModalUpdateAbsenceRecordMutation,
  useTimeReportEntryModalCreateSingularAbsenceRecordMutation,
  useTimeReportEntryModalCreateAbsenceRecordMutation,
}: MakeHooksConfigArg): RequiredHooks => ({
  gql: {
    mutations: {
      useTimeReportEntryModalCreateAbsenceRecordMutation: {
        hook: useTimeReportEntryModalCreateAbsenceRecordMutation,
        splitError: (response) => {
          if (isNil(response)) {
            return [null, null];
          }

          if ('adminCreate' in response) {
            return splitError(parseAccessRights(
              response.adminCreate.absenceRecord.createMany, AccessModes.assumeHandled
            ))
          }

          return splitError(response.userCreate.absenceRecord.createMany);
        },
      },
      useTimeReportEntryModalUpdateAbsenceRecordMutation: {
        hook: useTimeReportEntryModalUpdateAbsenceRecordMutation,
        splitError: (response) => {
          if (isNil(response)) {
            return [null, null];
          }

          if ('adminUpdate' in response) {
            return splitError(parseAccessRights(response.adminUpdate.absenceRecord.update, AccessModes.assumeHandled))
          }

          return splitError(response.userUpdate.absenceRecord.update);
        },
      },
      useTimeReportEntryModalCreateSingularAbsenceRecordMutation: {
        hook: useTimeReportEntryModalCreateSingularAbsenceRecordMutation,
        splitError: (response) => {
          if (isNil(response)) {
            return [null, null];
          }

          if ('adminCreate' in response) {
            return splitError(parseAccessRights(response.adminCreate.absenceRecord.create, AccessModes.assumeHandled))
          }

          return splitError(response.userCreate.absenceRecord.create);
        },
      },
      useCopyrightsDrawerMarkNoTransferMutation: {
        hook: useCopyrightsDrawerMarkNoTransferMutation,
        splitError: (response) => {
          if (isNil(response)) {
            return [null, null];
          }

          if ('contractSalaryDetails' in response) {
            return splitError(response.contractSalaryDetails.workTransferProtocol.markNoWorkToBeTransferedThisMonth);
          }

          return splitError(response.reporting.workTransferProtocolApi.markNoWorkToBeTransferedThisMonth);
        },
      },
      useCopyrightsDrawerCreateWorkTransferMutation: {
        hook: useCopyrightsDrawerCreateWorkTransferMutation,
        splitError: (response) => {
          if (isNil(response)) {
            return [null, null];
          }

          if ('contractSalaryDetails' in response) {
            return splitError(response.contractSalaryDetails.workTransferProtocol.create);
          }

          return splitError(response.reporting.workTransferProtocolApi.create);
        },
      },
      useCopyrightsDrawerRemoveWorkTransferMutation: {
        hook: useCopyrightsDrawerRemoveWorkTransferMutation,
        splitError: (response) => {
          if (isNil(response)) {
            return [null, null];
          }

          if ('contractSalaryDetails' in response) {
            return splitError(response.contractSalaryDetails.workTransferProtocol.remove);
          }

          return splitError(response.reporting.workTransferProtocolApi.remove);
        },
      },
      useCopyrightsDrawerUpdateWorkTransferMutation: {
        hook: useCopyrightsDrawerUpdateWorkTransferMutation,
        splitError: (response) => {
          if (isNil(response)) {
            return [null, null];
          }

          if ('contractSalaryDetails' in response) {
            return splitError(response.contractSalaryDetails.workTransferProtocol.update);
          }

          return splitError(response.reporting.workTransferProtocolApi.update);
        },
      },
      useInvoicesDrawerCreateInvoiceMutation: {
        hook: useInvoicesDrawerCreateInvoiceMutation,
        splitError: (response) => {
          if (isNil(response)) {
            return [null, null];
          }

          if ('contractSalaryDetails' in response) {
            return splitError(response.contractSalaryDetails.invoiceDetails.create);
          }

          return splitError(response.reporting.invoiceDetailsApi.createInvoiceDetails);
        },
      },
      useInvoicesDrawerRemoveInvoiceMutation: {
        hook: useInvoicesDrawerRemoveInvoiceMutation,
        splitError: (response) => {
          if (isNil(response)) {
            return [null, null];
          }

          if ('contractSalaryDetails' in response) {
            return splitError(response.contractSalaryDetails.invoiceDetails.remove);
          }

          return splitError(response.reporting.invoiceDetailsApi.removeInvoiceDetails);
        },
      },
      useInvoicesDrawerUpdateInvoiceMutation: {
        hook: useInvoicesDrawerUpdateInvoiceMutation,
        splitError: (response) => {
          if (isNil(response)) {
            return [null, null];
          }

          if ('contractSalaryDetails' in response) {
            return splitError(response.contractSalaryDetails.invoiceDetails.update);
          }

          return splitError(response.reporting.invoiceDetailsApi.updateInvoiceDetails);
        },
      },
    },
    queries: {
      useCopyrightsDrawerCopyrightsLazyQuery: {
        hook: useCopyrightsDrawerCopyrightsLazyQuery,
        splitError: null,
      },
      useAdditionalCostsLazyQuery: {
        hook: useAdditionalCostsLazyQuery,
        splitError: (response) => splitError(parseAccessRights(response?.contract, AccessModes.assumeHandled)),
      },
      useTimeReportWorkTimeInsightsLazyQuery: {
        hook: useTimeReportWorkTimeInsightsLazyQuery,
        splitError: (response) => splitError(parseAccessRights(response?.timeReport, AccessModes.assumeHandled)),
      },
      useDirectReportingStructureQuery: {
        hook: useDirectReportingStructureQuery,
        splitError: (response) => {
          if (isNil(response)) {
            return [null, null];
          }

          if ('profile' in response) {
            return splitError(response.profile);
          }

          return splitError(parseAccessRights(response.user, AccessModes.assumeHandled));
        },
      },
      usePreviousMonthSalaryStateQuery: {
        hook: usePreviousMonthSalaryStateQuery,
        splitError: null,
      },
      useTimeReportStats: {
        hook: useTimeReportStats,
        splitError: null,
      },
      useCopyrightsTrButtonQuery: {
        hook: useCopyrightsTrButtonQuery,
        splitError: null,
      },
      useTimeReportLeaveAndTrainingQuery: {
        hook: useTimeReportLeaveAndTrainingQuery,
        splitError: null,
      },
      useInvoicesDrawerInvoicesLazyQuery: {
        hook: useInvoicesDrawerInvoicesLazyQuery,
        splitError: null,
      },
      useTimeReportEntryModalLimitsQuery: {
        hook: useTimeReportEntryModalLimitsQuery,
        splitError: null,
      },
      useUserSpaceOwner: {
        hook: useUserSpaceOwner,
        splitError: (response) => {
          if (isNil(response)) {
            return [null, null];
          }

          if ('profile' in response) {
            return splitError(response.profile);
          }

          return splitError(response.user);
        },
      },
      useProjectColors: {
        hook: useProjectColors,
        splitError: (response) => {
          if (isNil(response)) {
            return [null, null];
          }

          if ('projects' in response) {
            return splitError(response.projects);
          }

          return splitError(response.getUserProjects);
        },
      },
      useOnCallMultipliers: {
        hook: useOnCallMultipliers,
        splitError: (response) => splitError(response?.onCallMultipliers),
      },
      useTimeReportFinances: {
        hook: useTimeReportFinances,
        splitError: null,
      },
      useBenefitSectionsAssignedBenefitVariantsQuery: {
        hook: useBenefitSectionsAssignedBenefitVariantsQuery,
        splitError: null,
      },
    },
  },
});
